/* eslint-disable dot-notation */
/* eslint-disable react/no-children-prop */
import React, {
  useState,
  useEffect,
  useMemo,
  forwardRef,
  createRef,
} from "react";
import Form, { IChangeEvent, UiSchema } from "@rjsf/core";
import _ from "lodash";
import { useContextSelector } from "use-context-selector";

import { css } from "@emotion/react";
import * as loftWidgets from "./components/LoftWidgets";
import * as loftFields from "./components/LoftFields";
import { CacheConfig, ProposalJsonFormProps, FormContext } from "./types";

import {
  getCustomFieldTemplate,
  getUiFieldForElement,
  getUiOptionsForElement,
  getUiWidgetForElement,
  transformErrors,
  emptyAddressObject,
} from "./utils";
import { JsonSchemaDataContext } from "./JsonSchemaDataContext";
import { DisabledForm } from "./DisabledForm/DisabledForm";

export type ProposalJsonFormPropsMetaItemWrapper = {
  versionId: string;
  canEdit: boolean;
  isSeller: boolean;
  cacheConfig?: CacheConfig;
  onChange: () => void;
  onBlur?: ProposalJsonFormProps["onBlur"];
};

export const ProposalJsonFormMetaItem = ({
  versionId,
  cacheConfig,
  ...props
}: ProposalJsonFormPropsMetaItemWrapper) => {
  const form = useContextSelector(
    JsonSchemaDataContext,
    context => context.metaForms[versionId]
  );
  const metaFormReferenceMap = useContextSelector(
    JsonSchemaDataContext,
    context => context.metaFormReferenceMap
  );
  const ref = createRef<Form<any>>();
  useEffect(() => {
    if (ref.current) {
      metaFormReferenceMap[versionId] = ref.current;
    }
  }, [ref]);

  const { formData, touchAllErrors } = form ?? {};
  const { config, formDataSchema } = form?.form ?? {};

  const cacheConfigObject = cacheConfig
    ? {
        id: versionId + cacheConfig.id,
        invalidationKey: cacheConfig.invalidationKey,
      }
    : undefined;

  return (
    <ProposalJsonForm
      ref={ref}
      cacheConfig={cacheConfigObject}
      id={versionId}
      elementArrangement={config?.elementArrangement as any}
      elements={config?.elements as any}
      formData={formData}
      schema={formDataSchema}
      touchAllErrors={touchAllErrors}
      {...props}
    />
  );
};

const ProposalJsonForm = forwardRef<Form<any>, ProposalJsonFormProps>(
  (
    {
      canEdit,
      children,
      elementArrangement,
      elements,
      id,
      isSeller,
      formData,
      onBlur,
      schema: originalSchema,
      touchAllErrors,
      onChange,
      cacheConfig,
      onClose,
      scale,
      maxWidth,
      ...otherProps
    },
    ref
  ) => {
    const [touchedFields, setTouchedFields] = useState({});
    const valueModel = useContextSelector(
      JsonSchemaDataContext,
      context => context.valueModel
    );

    const formDefaults = useContextSelector(
      JsonSchemaDataContext,
      context => context.formDefaults
    );

    const entries = useContextSelector(
      JsonSchemaDataContext,
      context => context.entries
    );

    const allTouched = (isTouched = true) =>
      Object.keys(schema.properties).reduce(
        (memo, key) => ({
          [key]: isTouched,
          ...memo,
        }),
        {}
      );

    useEffect(() => {
      if (touchAllErrors) {
        setTouchedFields(allTouched());
      }
    }, [touchAllErrors]);
    const schema = useMemo(() => {
      const result = _.cloneDeep(originalSchema);
      elements.forEach(element => {
        const { key, role } = element;
        if (typeof formDefaults[role] !== "undefined") {
          _.set(result, ["properties", key, "default"], formDefaults[role]);
        }
      });
      return result;
    }, [originalSchema, elements, formDefaults]);

    const uiSchema = useMemo<UiSchema>(() => {
      const nextUISchema = {
        "ui:order": [...elementArrangement],
      };

      elements.forEach(element => {
        const {
          buyerAccess,
          key,
          optionSourceExternalId,
          optionType,
          sellerAccess,
          type,
        } = element;

        // Flags on Retool
        const accessType = (isSeller ? sellerAccess : buyerAccess) || {
          editable: true,
          hidden: false,
        };
        const { editable, hidden } = accessType;

        const shouldHideElement = hidden;
        // Here we allow to set as not editable on Retool even if allowed on (Buyer|Seller)ProposalByStatus
        const shouldDisableElement = !canEdit || !editable;

        const isElementTouched = _.get(touchedFields, key, false);
        const elementUISchema = {
          "ui:name": key,
          "ui:touched": isElementTouched,
          "ui:widget": getUiWidgetForElement(element),
          "ui:FieldTemplate": getCustomFieldTemplate(),
          "ui:field": getUiFieldForElement(element),
          "ui:options": getUiOptionsForElement(element),
          "ui:placeholder": element.placeholder,
        } as {
          [key: string]: any;
        };

        if (
          ["SELECT", "MULTI_SELECT"].includes(type) &&
          optionSourceExternalId &&
          optionType === "PROFILE_CATEGORY"
        ) {
          const enumOptOverride = _.chain(entries)
            .filter(entry => entry?.category?.id === optionSourceExternalId)
            .map(entry => entry.tags)
            .flatten()
            .map(tag => ({ value: tag.name, label: tag.name }))
            .value();

          if (
            _.get(formData, "key") &&
            !_.find(enumOptOverride, { value: formData[key] })
          ) {
            enumOptOverride.push({
              value: formData[key],
              label: formData[key],
            });
          }
          if (!enumOptOverride.length) {
            enumOptOverride.push({ value: "", label: "None" });
          }
          elementUISchema["ui:options"].enumOptOverride = enumOptOverride;
          elementUISchema["ui:field"] = "StringField";
        }
        if (shouldHideElement) {
          elementUISchema["ui:widget"] = "hidden";
        }
        elementUISchema["ui:disabled"] = shouldDisableElement;
        elementUISchema.classNames = `key__${key}`;
        elementUISchema.description = element.description;
        nextUISchema[key] = elementUISchema;
      });

      return nextUISchema;
    }, [touchedFields, elements, elementArrangement]);

    const handleBlur = (key, value) => {
      setTouchedFields({ ...touchedFields, [key]: true });
      if (onBlur) {
        onBlur({ element: { id, key, value }, formData });
      }
    };

    const handleError = () => {
      // Touch all fields if there are errors during submitting
      setTouchedFields(allTouched());
    };

    const getCSS = (currentFormData: any): string => {
      let result = "";
      elements.forEach(element => {
        let isHidden = false;
        if (element.hideIf) {
          try {
            isHidden =
              _.template(`\${${element.hideIf}}`)(currentFormData) === "true";
          } catch (err) {
            console.error(err);
          }
        }
        if (isHidden) {
          result += `.key__${element.key} { display: none; } `;
        }
      });
      return result;
    };
    const [hiddenCSS, setHiddenCSS] = useState(getCSS(formData));
    const [formInstanceData, setFormInstanceData] = useState({
      resetKey: id,
      formData: formData || {},
    });

    useEffect(() => {
      let nextFormData = formData || {};
      if (cacheConfig) {
        const currentData = window.localStorage.getItem(cacheConfig.id);
        if (currentData) {
          // If the invalidation key matches, load the form data from local storage into the state.
          const parsedData = JSON.parse(currentData);
          if (parsedData.invalidationKey === cacheConfig.invalidationKey) {
            if (Object.keys(parsedData.value).length) {
              nextFormData = parsedData.value;
              if (onChange && !_.isEqual(nextFormData, formData)) {
                onChange({ formData: nextFormData } as any);
              }
            } else {
              window.localStorage.removeItem(cacheConfig.id);
            }
          } else {
            // Otherwise, restart the cache.
            const newCacheData = {
              invalidationKey: cacheConfig.invalidationKey,
              value: nextFormData,
            };
            window.localStorage.setItem(
              cacheConfig.id,
              JSON.stringify(newCacheData)
            );
          }
        }
      }
      Object.entries(nextFormData).forEach(([key, value]) => {
        const isRequired = schema.required.indexOf(key) !== -1;
        const isAddressField = uiSchema[key]?.["ui:field"] === "AddressField";
        // set the address field value to an empty object removes the required error
        // allowing sellers do not depend on the buyer action to submit the proposal
        if (typeof value === "object" && _.isEmpty(value)) {
          if (isSeller && isAddressField && isRequired) {
            nextFormData[key] = emptyAddressObject;
          } else {
            nextFormData[key] = undefined;
          }
        }
      });
      setFormInstanceData({
        resetKey: id,
        formData: nextFormData,
      });
      setHiddenCSS(getCSS(nextFormData));
    }, [id, JSON.stringify(formData), cacheConfig?.id]);

    useEffect(() => {
      setTouchedFields(allTouched(false));
    }, [id]);

    const clearWhenChangedElements = _.filter(
      elements,
      e => !!e.clearWhenChanged?.length
    );
    const handleChange = (e: IChangeEvent, es) => {
      let nextFormData = _.cloneDeep(e.formData);
      let nextTouchedFields = _.cloneDeep(touchedFields);
      clearWhenChangedElements.forEach(element => {
        const elementHasChanged = !_.eq(
          formInstanceData.formData[element.key],
          e.formData[element.key]
        );
        if (elementHasChanged) {
          nextFormData = _.omit(nextFormData, element.clearWhenChanged || []);
          nextTouchedFields = _.omit(
            nextTouchedFields,
            element.clearWhenChanged || []
          );
        }
      });
      setTouchedFields(nextTouchedFields);
      setFormInstanceData({
        resetKey: formInstanceData.resetKey,
        formData: nextFormData,
      });
      setHiddenCSS(getCSS(nextFormData));
      if (onChange) {
        e.formData = nextFormData;
        onChange(e, es);
      }
      updateCache(nextFormData);
    };

    const updateCache = (formData: any) => {
      if (cacheConfig) {
        const formCache = window.localStorage.getItem(cacheConfig.id);

        let localData = { value: {}, invalidationKey: "" };

        if (formCache) {
          const parsedCache = JSON.parse(formCache);
          localData = {
            ...parsedCache,
            value: formData,
          };
          window.localStorage.setItem(
            cacheConfig.id,
            JSON.stringify(localData)
          );
        } else {
          localData = {
            invalidationKey: valueModel?.updatedAt,
            value: formData,
          };
          window.localStorage.setItem(
            cacheConfig.id,
            JSON.stringify(localData)
          );
        }
      }
    };

    if (!canEdit) {
      return (
        <DisabledForm
          elements={elements}
          schema={schema}
          formData={formInstanceData.formData}
          elementArrangement={elementArrangement}
          onClose={onClose}
        />
      );
    }

    const formContext: FormContext = {
      formData: formInstanceData.formData,
      canEdit,
      isSeller,
      buyerUserId: valueModel?.buyerUserId,
      touchAllErrors,
      scale,
      maxWidth,
    };

    return (
      <Form
        ref={ref}
        key={formInstanceData.resetKey}
        id={id}
        css={css(hiddenCSS)}
        autoComplete="off"
        formData={formInstanceData.formData}
        liveValidate
        onBlur={handleBlur}
        onError={handleError}
        schema={schema}
        showErrorList={false}
        transformErrors={errors => transformErrors(errors, schema)}
        uiSchema={uiSchema}
        widgets={loftWidgets}
        fields={loftFields}
        children={children || true}
        noHtml5Validate
        formContext={formContext}
        onChange={handleChange}
        {...otherProps}
      />
    );
  }
);

ProposalJsonForm.defaultProps = {
  onBlur: undefined,
};

export default ProposalJsonForm;
